import React from "react";

import { VintomError } from "src/api/model/error/VintomError";
import { I18n } from "../../../../i18n/i18n";
import { PlayerControls } from "../../../../model/playerConfiguration/PlayerControls";
import { ScenarioElement } from "../../../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../../../model/playerConfiguration/Subtitle";
import { QualityLevel } from "../../../../model/qualityLevel/QualityLevel";
import { ScenarioPath } from "../../../../model/scenarioPath/ScenarioPath";
import { SubtitlesText } from "../../../../model/subtitles/SubtitlesText";
import { ScenarioElementPlaybackState } from "../../../../uiModel/playback/ScenarioElementPlaybackState";
import { SocialShareConfiguration } from "../../../../uiModel/socialShare/SocialShareConfiguration";
import { TooltipMetadata } from "../../../../uiModel/tooltip/TooltipMetadata";
import { TooltipStyle } from "../../../../uiModel/tooltip/TooltipStyle";
import { TooltipTarget } from "../../../../uiModel/tooltip/TooltipTarget";
import { SubtitlesDisplayComponent } from "../common/SubtitlesDisplayComponent";
import { TooltipComponent } from "../common/TooltipComponent";
import { DesktopMenuComponent } from "./DesktopMenuComponent";
import { DesktopOverlayComponent } from "./DesktopOverlayComponent";
import { QualityLevelsComponent } from "./QualityLevelsComponent";

import "@styles/c-desktopControlsLayer.scss";

interface Props {
  i18n: I18n;
  configuration: PlayerControls;
  isAdminMode: boolean;
  bandwidth: number;
  qualityLevels: QualityLevel[];
  isLoading: boolean;
  loaderTrigger: string;
  isCTADisplayed: boolean;
  socialShareConfiguration: SocialShareConfiguration;

  playbackState: ScenarioElementPlaybackState;
  onPlay: () => void;
  onPause: () => void;
  onReplay: () => void;
  onSeekStart: () => void;
  onSeekEnd: () => void;

  scenarioPath: ScenarioPath;
  totalTime: number;
  ellapsedTime: number;
  bufferedTime: number;
  onSeekTo: (time: number) => void;
  onJumpToScenarioElement: (scenarioElement: ScenarioElement, progress: number) => void;

  isVolumeMuted: boolean;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onToggleMuted: () => void;
  onUnMute: () => void;

  subtitles: Subtitle[];
  selectedSubtitlesSrc?: string;
  subtitlesText?: SubtitlesText;
  onSubtitleSourceSelected: (subtitle?: Subtitle) => void;

  playbackSpeed: number;
  onPlaybackSpeedChange: (speed: number) => void;

  isDebugMode: boolean;
  onToggleDebugMode: () => void;

  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;

  message?: string | VintomError;
  onSendError: (error: VintomError) => void;
  playIconOnPoster: boolean;
  disableIcon: boolean;
}

interface State {
  isControlsShown: boolean;
  tooltipMetadata?: TooltipMetadata;
}

export class DesktopControlsLayerComponent extends React.Component<Props, State> {
  private static SHOW_CONTROLS_TIMEOUT = 1500;
  private static SHOW_TOOLTIP_TIMEOUT = 1000;
  private static TOOLTIP_MODE_THRESHOLD = 2000;

  private lastNonImmediateTooltipTime?: number;
  private tooltipTimeoutHandle?: number;
  private hideControlsTimeoutHandle?: number;

  constructor(props: Props) {
    super(props);
    this.state = {
      isControlsShown: false,
    };
  }

  private get isControlsDisplayed() {
    return (
      this.state.isControlsShown ||
      this.props.playbackState === "seekingAfterPlay" ||
      this.props.playbackState === "seekingAfterPause" ||
      !!this.props.message
    );
  }
  render() {
    const {
      i18n,
      configuration,
      isAdminMode,
      bandwidth,
      qualityLevels,
      isLoading,
      loaderTrigger,
      isCTADisplayed,
      socialShareConfiguration,

      playbackState,
      onPlay,
      onPause,
      onReplay,
      onSeekStart,
      onSeekEnd,

      scenarioPath,
      totalTime,
      ellapsedTime,
      bufferedTime,
      onSeekTo,
      onJumpToScenarioElement,

      isVolumeMuted,
      volume,
      onVolumeChange,
      onToggleMuted,
      onUnMute,

      playbackSpeed,
      onPlaybackSpeedChange,

      isDebugMode,
      onToggleDebugMode,

      subtitles,
      selectedSubtitlesSrc,
      subtitlesText,
      onSubtitleSourceSelected,

      isFullscreenSupported,
      isFullscreen,
      onToggleFullscreen,

      message,
      onSendError,
      disableIcon,
    } = this.props;

    return (
      <div className="c-desktopControlsLayer">
        <div
          className={`c-desktopControlsLayer__overlayLayer ${
            configuration.playPauseEnabled ? "" : "c-desktopControlsLayer__overlayLayer--hidden"
          }`}
        >
          <DesktopOverlayComponent
            i18n={i18n}
            isCTADisplayed={isCTADisplayed}
            isPlayPauseEnabled={configuration.playPauseEnabled}
            playbackState={playbackState}
            isLoading={isLoading}
            loaderTrigger={loaderTrigger}
            isVolumeMuted={isVolumeMuted}
            onPlay={onPlay}
            onReplay={onReplay}
            onUnMute={onUnMute}
            message={message}
            onSendError={onSendError}
            playIconOnPoster={configuration.playIconOnPoster}
            disableIcon={disableIcon}
          />
        </div>
        {subtitlesText && (
          <div className="c-desktopControlsLayer__subtitlesLayer">
            <div
              className="c-desktopControlsLayer__subtitlesTopSpacer"
              style={{
                flexBasis: isFullscreen ? "100%" : `${subtitlesText.verticalPosition}%`,
              }}
            />
            <SubtitlesDisplayComponent className="c-desktopControlsLayer__subtitles" subtitlesText={subtitlesText.text} />
            <div
              className={`c-desktopControlsLayer__subtitlesBottomSpacer ${
                this.isControlsDisplayed ? "c-desktopControlsLayer__subtitlesBottomSpacer--withControls" : ""
              }`}
            />
          </div>
        )}
        <div className="c-desktopControlsLayer__menuLayer">
          <div className="c-desktopControlsLayer__video">
            <div className="c-desktopControlsLayer__tooltipLayer">
              {this.state.tooltipMetadata && <TooltipComponent isDesktop={true} {...this.calculateTooltipModel()} />}
            </div>
          </div>
          {scenarioPath && configuration.controlbarEnabled && (
            <div className={`c-desktopControlsLayer__menu ${this.isControlsDisplayed ? "" : "c-desktopControlsLayer__menu--hidden"}`}>
              <DesktopMenuComponent
                i18n={i18n}
                configuration={configuration}
                isDisabled={!!message}
                isDisplayed={this.isControlsDisplayed}
                isAdminMode={isAdminMode}
                socialShareConfiguration={socialShareConfiguration}
                onMouseEnter={() => this.handleMouseEnteredMenu()}
                onMouseLeave={() => this.handleMouseLeftMenu()}
                playbackState={playbackState}
                onPlay={onPlay}
                onPause={onPause}
                onReplay={onReplay}
                onSeekStart={onSeekStart}
                onSeekEnd={onSeekEnd}
                scenarioPath={scenarioPath}
                totalTime={totalTime}
                ellapsedTime={ellapsedTime}
                bufferedTime={bufferedTime}
                onSeekTo={onSeekTo}
                onJumpToScenarioElement={onJumpToScenarioElement}
                isVolumeMuted={isVolumeMuted}
                volume={volume}
                onVolumeChange={onVolumeChange}
                onToggleMuted={onToggleMuted}
                subtitles={subtitles}
                onSubtitleSourceSelected={onSubtitleSourceSelected}
                selectedSubtitlesSrc={selectedSubtitlesSrc}
                playbackSpeed={playbackSpeed}
                onPlaybackSpeedChange={onPlaybackSpeedChange}
                isDebugMode={isDebugMode}
                onToggleDebugMode={onToggleDebugMode}
                isFullscreenSupported={isFullscreenSupported}
                isFullscreen={isFullscreen}
                onToggleFullscreen={() => {
                  this.hideTooltip();
                  onToggleFullscreen();
                }}
                onCancelTooltip={() => this.hideTooltip()}
                onRequestTooltip={(event) => this.showTooltip(event)}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  hidePlayerInfoTooltip() {
    if (this.state.tooltipMetadata && this.state.tooltipMetadata.target === "playerInfo") {
      this.hideTooltip();
    }
  }

  showPlayerInfoTooltip(x: number, y: number) {
    this.showTooltip({
      x,
      y,
      target: "playerInfo",
    });
  }

  handleUserInteractedWithPlayer() {
    this.hidePlayerInfoTooltip();
    this.clearTimerAndShowControls();
    this.setHideControlsTimer();
  }

  handleUserInteractionEnded() {
    this.hideControls();
  }

  handleUserClickedPlayer() {
    const { playbackState, onPlay, onPause, onReplay } = this.props;
    switch (playbackState) {
      case "paused":
        onPlay();
        break;
      case "playing":
        onPause();
        break;
      case "scenarioFinished":
        onReplay();
        break;
      default:
      // do nothing
    }
  }

  private calculateTooltipModel() {
    const { i18n, playbackState, isVolumeMuted, isFullscreen } = this.props;
    const { tooltipMetadata } = this.state;

    if (!tooltipMetadata) {
      return;
    }

    const coords = (({ x, y }) => ({ x, y }))(tooltipMetadata);
    switch (this.state.tooltipMetadata.target) {
      case "playerInfo":
        return {
          ...coords,
          text: i18n.poweredByVintom,
          licenceAdditionalText: i18n.licenceAdditionalText,
          textLicence: i18n.licenceLabel,
          linkLicence: i18n.linkLicence,
          style: "accent" as TooltipStyle,
        };
      case "seekbarPart":
        return {
          ...coords,
          text: tooltipMetadata.text,
          style: "accent" as TooltipStyle,
        };
      case "seekbarSlider":
        return {
          ...coords,
          text: tooltipMetadata.text,
          style: "light" as TooltipStyle,
          pointerEvents: "none" as "none",
        };
      case "playbackControl":
        const playbackStateToTooltipText = {
          finished: i18n.replay,
          paused: i18n.play,
          playing: i18n.pause,
        };
        return {
          ...coords,
          text: playbackStateToTooltipText[playbackState],
          style: "dark" as TooltipStyle,
        };
      case "volumeControl":
        return {
          ...coords,
          text: isVolumeMuted ? i18n.unmute : i18n.mute,
          style: "dark" as TooltipStyle,
        };
      case "shareControl":
        return {
          ...coords,
          text: i18n.share,
          style: "dark" as TooltipStyle,
        };
      case "fullscreenControl":
        return {
          ...coords,
          text: isFullscreen ? i18n.exitFullscreen : i18n.fullscreen,
          style: "dark" as TooltipStyle,
        };
      case "subtitlesControl":
        return {
          ...coords,
          text: i18n.subtitles,
          style: "dark" as TooltipStyle,
        };
      default:
        return;
    }
  }

  private showTooltip(tooltipMetadata: TooltipMetadata) {
    if (this.tooltipTimeoutHandle) {
      clearTimeout(this.tooltipTimeoutHandle);
    }

    const timeFromLastNonImmediateTarget = Date.now() - this.lastNonImmediateTooltipTime;

    if (
      this.isTooltipTargetImmediate(tooltipMetadata.target) ||
      timeFromLastNonImmediateTarget < DesktopControlsLayerComponent.TOOLTIP_MODE_THRESHOLD
    ) {
      this.setState({
        tooltipMetadata,
      });
    } else {
      this.tooltipTimeoutHandle = window.setTimeout(
        () => this.setState({ tooltipMetadata }),
        DesktopControlsLayerComponent.SHOW_TOOLTIP_TIMEOUT,
      );
    }
  }

  private isTooltipTargetImmediate(target: TooltipTarget) {
    return ["playerInfo", "seekbarPart", "seekbarSlider"].indexOf(target) > -1;
  }

  private hideTooltip() {
    const { tooltipMetadata } = this.state;
    if (tooltipMetadata && !this.isTooltipTargetImmediate(tooltipMetadata.target)) {
      this.lastNonImmediateTooltipTime = Date.now();
    }
    if (this.tooltipTimeoutHandle) {
      clearTimeout(this.tooltipTimeoutHandle);
    }
    // tslint:disable:align
    setTimeout(() => {
      this.setState({
        tooltipMetadata: undefined,
      });
    }, 3000);
    // tslint:enable:align
  }

  private handleMouseEnteredMenu() {
    this.clearTimerAndShowControls();
  }

  private handleMouseLeftMenu() {
    this.setHideControlsTimer();
  }

  private setHideControlsTimer() {
    this.hideControlsTimeoutHandle = window.setTimeout(() => this.hideControls(), DesktopControlsLayerComponent.SHOW_CONTROLS_TIMEOUT);
  }

  private clearTimerAndShowControls() {
    if (this.hideControlsTimeoutHandle !== undefined) {
      clearTimeout(this.hideControlsTimeoutHandle);
    }
    this.setState({
      isControlsShown: true,
    });
  }

  private hideControls() {
    this.setState({
      isControlsShown: false,
    });
  }
}
